import { useEffect, useMemo } from "react";
import { PageHeader } from "features/MarketingMaterials/pages/components/PageHeader/PageHeader.tsx";
import ProgressStepper from "components/Common/Stepper/Stepper.tsx";
import {
	MerchAccountSelect,
	ViewCartPageWrapper
} from "features/MarketingMaterials/pages/Checkout/ViewCartPage.styles.ts";
import useMarketingMaterialsCart from "features/MarketingMaterials/hooks/useMarketingMaterialsCart.ts";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import { ViewCartSkeleton } from "features/MarketingMaterials/pages/Checkout/components/ViewCartSkeleton/ViewCartSkeleton.tsx";
import { useGetMerchandiseAccountsQuery } from "features/api/accountApi.ts";
import AutoComplete from "components/Common/Autocomplete/Autocomplete.tsx";
import { sortedAccounts } from "components/NewOrders/utils/NewOrderUtils.tsx";
import { models } from "types/api/viewModels.ts";
import { CheckoutContainer, CheckoutHeaderItem } from "features/MarketingMaterials/pages/Checkout/Checkout.styles.ts";
import { CartItemDetails } from "features/MarketingMaterials/pages/Checkout/components/CartItemDetails.tsx";
import { ViewCartFloatingBar } from "features/MarketingMaterials/pages/Checkout/components/ViewCartFloatingBar/ViewCartFloatingBar.tsx";
import { SelectAccountNumberText } from "constants/text.ts";

export const ViewCartPage = () => {
	const {
		cartData,
		validation,
		isShoppingCartLoading,
		selectedMerchAccount,
		isMerchAccountSelectionError,
		setSelectedMerchAccount,
		getTotalPrice
	} = useMarketingMaterialsCart();
	const { data: user } = useGetUserDetailsQuery();
	const {
		data: merchAccounts,
		isLoading: isLoadingMerchAccounts,
		isSuccess: isMerchAccountsSuccess
	} = useGetMerchandiseAccountsQuery();
	const merchAccountOptions = useMemo(() => sortedAccounts(merchAccounts) ?? [], [merchAccounts]);

	useEffect(() => {
		if (!isMerchAccountsSuccess) {
			return;
		}

		const selectedAccountIsAvailable = merchAccountOptions.some(
			(account) => account.accountId === selectedMerchAccount?.accountId
		);
		if (selectedMerchAccount && !selectedAccountIsAvailable) {
			setSelectedMerchAccount(null);
			return;
		}

		if (selectedAccountIsAvailable) {
			return;
		}

		const budgetAccounts = merchAccountOptions.filter((account) => account.hasMerchandiseBudget);
		let defaultAccount: models["CustomerAccountViewModel"] | null = null;
		if (merchAccountOptions.length === 1) {
			defaultAccount = merchAccountOptions[0] ?? null;
		} else if (budgetAccounts.length === 1) {
			defaultAccount = budgetAccounts[0] ?? null;
		}

		if (defaultAccount) {
			setSelectedMerchAccount(defaultAccount);
		}
	}, [isMerchAccountsSuccess, merchAccountOptions, selectedMerchAccount, setSelectedMerchAccount]);

	return (
		<ViewCartPageWrapper>
			<PageHeader cartData={cartData} />
			<CheckoutContainer>
				<div>
					<CheckoutHeaderItem>
						<h3>Checkout</h3>
						<ProgressStepper data-testid="marketing-materials-header-stepper" />
					</CheckoutHeaderItem>
					<CheckoutHeaderItem>
						<h4>View Cart</h4>
						{!isShoppingCartLoading && (
							<MerchAccountSelect>
								<AutoComplete
									value={selectedMerchAccount}
									disableClearable
									isLoading={isLoadingMerchAccounts}
									label="Account Number"
									getOptionLabel={(option: models["CustomerAccountViewModel"]) =>
										option?.accountNumber && option?.accountName
											? `${option.accountNumber} - ${option.accountName}`
											: (option?.accountNumber ?? "")
									}
									onChange={(_event, value) => {
										setSelectedMerchAccount(value);
									}}
									options={merchAccountOptions}
									dataTestId="select-merch-account-number"
									errorText={SelectAccountNumberText}
									isError={isMerchAccountSelectionError}
									isOptionEqualToValue={(option, value) => option?.accountId === value?.accountId}
								/>
							</MerchAccountSelect>
						)}
					</CheckoutHeaderItem>
				</div>
				{isShoppingCartLoading ? (
					<ViewCartSkeleton />
				) : (
					<CartItemDetails
						showBanner
						showInfoBanner={false}
						validation={validation}
						cartItems={cartData.cartItems}
						userRole={user?.role}
						isReviewOrConfirmation={false}
						subTotal={getTotalPrice()}
					/>
				)}
			</CheckoutContainer>
			<ViewCartFloatingBar />
		</ViewCartPageWrapper>
	);
};
